import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authAPI, getToken, removeToken, removeExternalSession, PublicUser } from '../utils/api';

type User = PublicUser;

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  requestRegistrationCode: (
    name: string,
    email: string,
    password: string,
    password_confirmation: string
  ) => Promise<{ verification_id: number; expires_at: string }>;
  verifyRegistrationCode: (verification_id: number, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Проверка авторизации при загрузке
  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (token) {
        try {
          const response = await authAPI.getUser();
          if (response.success && response.user) {
            setUser(response.user);
          }
        } catch (error) {
          console.error('Ошибка проверки авторизации:', error);
          const status = (error as { status?: number })?.status;
          if (status === 401) {
            removeToken();
            removeExternalSession();
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const refreshUser = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const response = await authAPI.getUser();
      if (response.success && response.user) {
        setUser(response.user);
      }
    } catch (error) {
      console.error('Ошибка обновления профиля:', error);
    }
  }, []);

  // Актуальный статус подписки при возврате на вкладку (без удаления данных)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && getToken()) {
        refreshUser();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    const response = await authAPI.login(email, password);
    if (response.success && response.user) {
      setUser(response.user);
    } else {
      throw new Error('Ошибка входа');
    }
  };

  const requestRegistrationCode = async (
    name: string,
    email: string,
    password: string,
    password_confirmation: string
  ) => {
    const response = await authAPI.requestRegistrationCode(name, email, password, password_confirmation);
    if (response.success && response.verification_id) {
      return { verification_id: response.verification_id, expires_at: response.expires_at };
    }
    throw new Error(response.message || 'Ошибка отправки кода');
  };

  const verifyRegistrationCode = async (verification_id: number, code: string) => {
    const response = await authAPI.verifyRegistrationCode(verification_id, code);
    if (response.success && response.user) {
      setUser(response.user);
    } else {
      throw new Error('Ошибка подтверждения кода');
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Ошибка выхода:', error);
    } finally {
      setUser(null);
      removeToken();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        isAdmin: !!user?.is_admin,
        login,
        requestRegistrationCode,
        verifyRegistrationCode,
        logout,
        refreshUser,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
